import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useSettings } from "../../lib/settings";
import AdminLayout from "../../components/app/AdminLayout";
import { Card, Loader } from "./Dashboard";
import { Send, Users, Link2 } from "lucide-react";

export default function Messages() {
  const dialog = useDialog();
  const { formatDate } = useSettings();
  const [statuses, setStatuses] = useState([]);
  const [auditions, setAuditions] = useState([]);
  const [filter, setFilter] = useState("all"); // "all" | "status:<key>" | "audition:<aid>"
  const [applicants, setApplicants] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [result, setResult] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    adminApi.get("/admin/statuses").then(({ data }) => setStatuses(data.items));
    adminApi.get("/admin/auditions").then(({ data }) => setAuditions(data.items || []));
  }, []);

  useEffect(() => {
    let url;
    if (filter.startsWith("audition:")) {
      url = `/admin/auditions/${filter.slice(9)}/attendees`;
    } else if (filter.startsWith("status:")) {
      url = `/admin/applicants?limit=500&status=${filter.slice(7)}`;
    } else {
      url = "/admin/applicants?limit=500";
    }
    adminApi.get(url).then(({ data }) => setApplicants(data.items || []));
    setSelected([]);
  }, [filter]);

  const insertVar = (v) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => `${b}${v}`); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + v + body.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + v.length; });
  };

  const send = async () => {
    if (selected.length === 0 || !subject || !body) return;
    if (!await dialog.confirm({ title: "Send bulk email?", description: `"${subject}" will be sent to ${selected.length} recipient${selected.length === 1 ? "" : "s"}.`, confirmLabel: "Send", destructive: false })) return;
    const { data } = await adminApi.post("/admin/applicants/bulk", { applicant_ids: selected, action: "send_email", subject, body });
    setResult(data.processed);
    setTimeout(() => setResult(null), 4000);
  };

  return (
    <AdminLayout title="Messages" subtitle="Send bulk emails to filtered applicant groups">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <Card title="Recipients">
            <label className="label-mini block mb-1.5">Send to</label>
            <select data-testid="msgs-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:border-blood">
              <optgroup label="By status">
                <option value="all">All applicants</option>
                {statuses.map((s) => <option key={s.key} value={`status:${s.key}`}>{s.label}</option>)}
              </optgroup>
              {auditions.length > 0 && (
                <optgroup label="By audition (attendees)">
                  {auditions.map((a) => (
                    <option key={a.id} value={`audition:${a.id}`}>
                      {formatDate(a.date)}{a.name ? ` — ${a.name}` : ""}{a.location ? ` (${a.location})` : ""}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {filter.startsWith("audition:") && (
              <p className="text-[11px] text-[#6B6862] -mt-1 mb-3" data-testid="msgs-audition-hint">Everyone who selected this audition date.</p>
            )}
            <div className="flex items-center gap-2 mb-2 text-xs">
              <Users size={12} className="text-blood" />
              <span className="text-[#A19D94]">{applicants.length} match · {selected.length} selected</span>
              <button onClick={() => setSelected(selected.length === applicants.length ? [] : applicants.map((a) => a.id))} className="ml-auto text-blood hover:underline">{selected.length === applicants.length && applicants.length > 0 ? "Clear" : "Select all"}</button>
            </div>
            <div className="border border-[#1a1a1a] max-h-96 overflow-y-auto">
              {applicants.length === 0 && <p className="p-3 text-xs text-[#6B6862]">No matching applicants.</p>}
              {applicants.map((a) => (
                <label key={a.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#1a1a1a] last:border-0 hover:bg-[#101010] cursor-pointer">
                  <input type="checkbox" checked={selected.includes(a.id)} onChange={() => setSelected((s) => s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id])} className="accent-[#D91616]" />
                  <span className="text-sm truncate">{a.full_name}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card title="Compose">
            {result !== null && <div className="mb-3 p-3 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] text-sm">Queued {result} email{result === 1 ? "" : "s"}.</div>}
            <label className="label-mini block mb-1.5">Subject</label>
            <input data-testid="msgs-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:border-blood" />
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-mini">Body <span className="text-[#6B6862] normal-case">(HTML allowed)</span></label>
              <button type="button" onClick={() => insertVar("{{character_sheets_url}}")} data-testid="msgs-insert-sheets" className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#A19D94] hover:text-blood border border-[#262626] hover:border-blood px-2 py-1">
                <Link2 size={12} /> Insert character sheets link
              </button>
            </div>
            <textarea ref={bodyRef} data-testid="msgs-body" rows={13} value={body} onChange={(e) => setBody(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-blood resize-none" />
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#6B6862]">
              <span>Variables:</span>
              {["{{first_name}}", "{{full_name}}", "{{character_sheets_url}}"].map((v) => (
                <button key={v} type="button" onClick={() => insertVar(v)} className="font-mono text-blood/80 hover:text-blood">{v}</button>
              ))}
            </div>
            <button onClick={send} data-testid="msgs-send-btn" disabled={selected.length === 0 || !subject || !body} className="mt-3 flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed">
              <Send size={12} /> Send to {selected.length} recipient{selected.length === 1 ? "" : "s"}
            </button>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
